import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/** A building, as the picker shows it. */
export interface FacilityOption {
  id: string;
  facility_name: string;
  facility_type: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
  /** What the visit rate keys on. Shown, so the standard is not a surprise. */
  residence_type_label: string | null;
  /** What a placement here is recorded as when nobody chooses (061). */
  placement_kind_label: string | null;
}

export interface ResidenceTypeOption {
  code: string;
  label: string;
  description: string | null;
  /** The last word of a home's name. Null for a facility type. */
  residence_noun: string | null;
  /** The agency rate in force today, visits a year. */
  visits_per_year: number | null;
}

export interface FacilityCandidate {
  id: string;
  facility_name: string;
  facility_type: string | null;
  residence_type_label: string | null;
  placement_kind_label: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  name_similarity: number;
  /** Across the whole agency, as a count. */
  clients_here: number;
}

export interface NewFacilityInput {
  facility_name: string;
  facility_type: string;
  street_address: string;
  city: string;
  state: string;
  zip?: string;
  county?: string;
  phone?: string;
  notes?: string;
  distinct_from: string[];
}

/** A client's home, recorded with a move or at intake. It has no name field. */
export interface NewHomeInput {
  property_type: string;
  tenure: 'Rented' | 'Owned' | 'Unknown';
  street_address: string;
  unit?: string;
  city: string;
  state: string;
  zip?: string;
  county?: string;
  notes?: string;
}

/**
 * Buildings, searched by name, another spelling or street.
 *
 * `/facilities`, not `/places`: the latter answers "where are my clients now",
 * so it would find a building only because somebody is already in it.
 */
export function useFacilitySearch(q: string, type: string | null) {
  const { getToken } = useSession();
  const query = new URLSearchParams({
    limit: '12',
    ...(q ? { q } : {}),
    ...(type ? { type } : {}),
  });
  return useQuery({
    queryKey: ['facilities', q, type],
    queryFn: () =>
      apiFetch<{ facilities: FacilityOption[] }>(`/facilities?${query.toString()}`, getToken),
    ...queryDefaults,
    placeholderData: (previous) => previous,
  });
}

export function useResidenceTypes(kind: 'facility' | 'home') {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['residence-types', kind],
    queryFn: () =>
      apiFetch<{ residence_types: ResidenceTypeOption[]; may_add: boolean }>(
        `/residence-types?for=${kind}`,
        getToken,
      ),
    ...queryDefaults,
    staleTime: 5 * 60_000,
  });
}

/** The duplicate guard: buildings already at an address. Asked, never cached. */
export function useFacilityCandidates() {
  const { getToken } = useSession();
  return useMutation({
    mutationFn: (a: { street_address: string; city: string; zip?: string; name: string }) => {
      const query = new URLSearchParams({
        street_address: a.street_address,
        city: a.city,
        name: a.name,
        ...(a.zip ? { zip: a.zip } : {}),
      });
      return apiFetch<{ candidates: FacilityCandidate[] }>(
        `/facilities/at-address?${query.toString()}`,
        getToken,
      );
    },
  });
}

export class AddressTaken extends ApiError {}

export function useCreateFacility() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewFacilityInput) => {
      try {
        return await apiFetch<{ facility: FacilityOption }>('/facilities', getToken, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input),
        });
      } catch (err) {
        // Somebody added a building at this address since the guard was asked.
        // The form asks again rather than showing a bare refusal.
        if (err instanceof ApiError && err.status === 409) {
          throw new AddressTaken(409, err.message, err.hint);
        }
        throw err;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
    },
  });
}

export interface PropertyRow {
  id: string;
  name: string;
  property_type: string | null;
  property_type_label: string | null;
  tenure: string;
  disposition: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  client_id: string;
  client_ref: string;
  legal_status: string;
  lives_here: boolean;
  mapped: boolean;
}

/** The Real Estate list. Tier 1 only for now; the route decides. */
export function useProperties() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['properties'],
    queryFn: () => apiFetch<{ properties: PropertyRow[] }>('/properties', getToken),
    ...queryDefaults,
  });
}

/** The words a home's name ends in, as `residence_name()` builds it (061). */
export function homeName(owner: string | null, city: string, noun: string | null): string {
  const who = owner?.trim() ? `${owner.trim()}'s` : "The client's";
  return [who, city.trim(), noun ?? 'Home'].filter(Boolean).join(' ');
}

/** One competing type for an untyped building, as the legacy records gave it. */
export interface TypeCandidate {
  type_code: string;
  label: string;
  /** False for a home type (Apartment, House): the "building" may be someone's home. */
  is_facility_type: boolean;
  occurrences: number;
  first_seen: string | null;
  last_seen: string | null;
  /** Where it was seen: "Visits 2021, Visits 2023". */
  seen_in: string;
}

export interface UntypedBuilding {
  facility_id: string;
  facility_name: string;
  street_address: string | null;
  city: string | null;
  review_case: 'No type found' | 'Conflicting types';
  candidates: TypeCandidate[];
  clients_here: number;
}

/** Buildings with no type (061). Tiers 1–2 and the two directors; the route decides. */
export function useUntypedBuildings(enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['facilities', 'needing-type'],
    queryFn: () =>
      apiFetch<{ facilities: UntypedBuilding[]; no_type: number; conflicting: number }>(
        '/facilities/needing-type',
        getToken,
      ),
    ...queryDefaults,
    enabled,
  });
}

export function useRecordFacilityType() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { facilityId: string; facility_type: string }) =>
      apiFetch<{ facility: FacilityOption }>(`/facilities/${a.facilityId}/type`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ facility_type: a.facility_type }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });
}
